/**
 * Formatting helpers for time and dates. Kept dumb on purpose — these
 * return strings, never React nodes.
 */

export function formatDuration(seconds: number): string {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

export function formatDateIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function today(): string {
  return formatDateIso(new Date());
}

export function daysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return formatDateIso(d);
}

export function monthsAgo(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return formatDateIso(d);
}

export function startOfWeekIso(): string {
  const d = new Date();
  const day = d.getDay() || 7; // Mon=1..Sun=7
  if (day !== 1) d.setHours(-24 * (day - 1));
  return formatDateIso(d);
}

export function startOfMonthIso(): string {
  const d = new Date();
  return formatDateIso(new Date(d.getFullYear(), d.getMonth(), 1));
}

export function startOfYearIso(): string {
  const d = new Date();
  return formatDateIso(new Date(d.getFullYear(), 0, 1));
}

export function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatUsdFromCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

export function formatRateFromCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return `${formatUsdFromCents(cents)}/h`;
}

export function parseRateInput(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export function rateInputValue(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
}

export function formatHours(hours: number): string {
  return `${hours.toFixed(2)}h`;
}

export function projectDisplayName(p: { display_name: string | null; name: string }): string {
  return (p.display_name && p.display_name.length > 0) ? p.display_name : p.name;
}
